import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { findLandingByPath, landingAlternates, landingPath } from '@/lib/marketing/localizedLandings';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  noIndex?: boolean;
}

/**
 * SEOHead - Comprehensive SEO meta tags component
 *
 * Features:
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Structured data (JSON-LD)
 * - Canonical URLs
 * - Robots meta tags
 *
 * @example
 * <SEOHead
 *   title="Online Booking for Doctors | Sloty"
 *   description="Streamline your medical practice with Sloty..."
 *   keywords="medical appointment booking, doctor scheduling software"
 *   canonical="https://slotyapp.com/for-doctors"
 *   ogImage="https://slotyapp.com/images/og-doctors.jpg"
 *   structuredData={doctorsSchema}
 * />
 */
const OG_LOCALES: Record<string, string> = { en: 'en_US', de: 'de_DE', 'pt-BR': 'pt_BR', 'pt-PT': 'pt_PT', pl: 'pl_PL' };

export const SEOHead = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://slotyapp.com/og-image.jpg',
  ogType = 'website',
  structuredData,
  noIndex = false,
}: SEOHeadProps) => {
  const { i18n } = useTranslation();
  const fullTitle = `${title} | Sloty`;
  const siteUrl = 'https://slotyapp.com';
  // A localized landing page (see localizedLandings.ts) is canonical in the current language,
  // with an hreflang alternate for every language it exists in.
  const landing = !noIndex && canonical ? findLandingByPath(canonical.replace(siteUrl, '')) : null;
  const fullCanonical = landing ? `${siteUrl}${landingPath(landing.id, i18n.language)}` : canonical || siteUrl;
  const alternates = landing ? landingAlternates(landing.id) : [];

  // Get og:locale based on current language
  const ogLocale = OG_LOCALES[i18n.language] ?? 'en_US';

  return (
    <Helmet>
      {/* Language */}
      <html lang={i18n.language} />

      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonical} />
      {alternates.map((alt) => (
        <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Sloty" />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@slotyapp" />
      <meta name="twitter:creator" content="@slotyapp" />
      <meta name="twitter:url" content={fullCanonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};
